import { HttpParams, httpResource } from '@angular/common/http';
import {
  afterRenderEffect,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  runInInjectionContext,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { IGetManyResponse } from '../../../../../utilities/http-utilities.service';
import { IForms } from '../../widget-add-edit-dialog.interface';

@Component({
  imports: [MatDropdown],
  selector: 'app-form-dropdown',
  styleUrl: './form-dropdown.component.scss',
  templateUrl: './form-dropdown.component.html',
})
export class FormDropdownComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  public readonly formControl = input.required<FormControl<IDropdownOption[]>>();
  public readonly selectedIdOnEdit = input.required<number | null>();
  public readonly isFormSubmitted = input.required<boolean>();

  public readonly searchValue = signal('');

  public readonly formDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.form'),
  };

  public readonly resource = httpResource<IGetManyResponse<IForms>>(() => {
    const selectedIdOnEdit = this.selectedIdOnEdit();

    return {
      method: 'GET',
      params: new HttpParams()
        .set('limit', 1000)
        .set(
          's',
          JSON.stringify({
            $and: [
              { name: { contL: this.searchValue() } },
              {
                $or: [{ isActive: { $eq: 1 } }, ...(selectedIdOnEdit ? [{ id: { $eq: selectedIdOnEdit } }] : [])],
              },
            ],
          }),
        )
        .set('sort', 'name,ASC'),
      reportProgress: true,
      url: 'forms',
    };
  });

  public readonly dropdownOptions = computed(() => {
    return this.resource.value()?.data.map((form) => ({ id: form.id, name: form.name })) ?? [];
  });
  private formControlValue: Signal<IDropdownOption[]> = signal([]);

  public readonly isLoading = computed(() => this.resource.status() !== 'resolved');

  constructor(private readonly injector: Injector) {
    afterRenderEffect(() => {
      const selectedFormId = this.formControlValue()?.[0]?.id;
      const matchingResponseData = this.resource.value()?.data.find(({ id }) => id === selectedFormId);
      const inactiveValidator = (): { inactiveOption: 'inactiveOption' } | null =>
        matchingResponseData && !matchingResponseData.isActive ? { inactiveOption: 'inactiveOption' } : null;

      if (matchingResponseData && !matchingResponseData.isActive) {
        this.formControl().addValidators(inactiveValidator);
        this.formControl().markAsDirty();
      } else {
        this.formControl().removeValidators(inactiveValidator);
      }

      this.formControl().updateValueAndValidity({ emitEvent: false });
    });
  }

  public ngOnInit(): void {
    this.formControlValue = runInInjectionContext(this.injector, () =>
      toSignal(this.formControl().valueChanges, {
        initialValue: this.formControl().value,
      }),
    );
  }
}
